// toppers UtilityPage body. Dispatches every block type in
// `UtilityPageBodyContent` (the Toolbox keys + utility extras: heading /
// paragraph / image / html / accordion_section / tab_nav / hidden_anchor_link).
//
// PER-BLOCK WRAPPER (load-bearing): the page CSS targets the
// `<div class="w-block-{type} block-{type}">` wrapper that Wagtail's
// `StreamBlock.render_basic` emits per top-level block, so each block renders
// through its own one-element StreamField with tagName="div" + attrs.className.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    HiddenAnchorLinkBlock as HiddenAnchorLinkBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
} from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { AccordionSection } from "../../blocks/AccordionSection";
import { HiddenAnchorLinkBlock } from "../../blocks/HiddenAnchorLinkBlock";
import { TabNav } from "../../blocks/TabNav";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

export const utilityComponentsMap = {
    ...toolboxComponentsMap,
    heading: (block: { value: Immutable<CharBlockValue> }) => (
        <>{block.value}</>
    ),
    paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
    image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
        <img
            src={block.value.url}
            alt={block.value.default_alt_text}
            width={block.value.width}
            height={block.value.height}
            loading="lazy"
            decoding="async"
        />
    ),
    html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
        <EditorContent>
            {(marker) => (
                <div
                    dangerouslySetInnerHTML={{ __html: block.value }}
                    {...marker}
                />
            )}
        </EditorContent>
    ),
    accordion_section: AccordionSection,
    tab_nav: TabNav,
    hidden_anchor_link: (block: {
        value: Immutable<HiddenAnchorLinkBlockValue>;
    }) => <HiddenAnchorLinkBlock value={block.value} />,
} as const;

type IBodyValue = templates.UtilityPageTemplate["page"]["body"]["value"];

export const UtilityPageBody = (
    props: Immutable<{ value: IBodyValue }>,
): React.JSX.Element => {
    return (
        <>
            {props.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={utilityComponentsMap}
                />
            ))}
        </>
    );
};
